import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Fragment } from 'react';

import { CreateParkAndRideFacilityForm } from '@/components/management/form/CreateParkAndRideFacilityForm';
import { FormGridLayout } from '@/components/management/form/formHelpers/FormGridLayout';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import { useAbility } from '@/hooks/useAbility';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import type { Station } from '@/modules/api/backend';
import { Action, Subject } from '@/modules/api/backend';
import {
  backendApiOrganizationsFindAllOptions,
  backendApiParkAndRideFacilitiesCreateMutation,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

interface CreateParkAndRideFacilityProps {
  stationId?: string;
  stationData?: Station;
}

export function CreateParkAndRideFacility({ stationId, stationData }: CreateParkAndRideFacilityProps) {
  const navigate = useNavigate();

  const { ability } = useAbility();

  const { data: organizations } = useSuspenseQuery(backendApiOrganizationsFindAllOptions());

  const { openSnackbar } = useSnackbarStore((state) => state.actions);

  const { mutateAsync } = useMutation({
    ...backendApiParkAndRideFacilitiesCreateMutation(),
    onSuccess: (data) => {
      navigate(
        stationId
          ? {
              to: '/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId',
              ignoreBlocker: true,
              params: { parkAndRideFacilityId: data.id, stationId },
            }
          : {
              to: '/management/stationless/$parkAndRideFacilityId',
              ignoreBlocker: true,
              params: { parkAndRideFacilityId: data.id },
            },
      );

      openSnackbar({
        color: 'success',
        message: 'Die Parkanlage wurde erfolgreich erstellt!',
      });
    },
    onError: () => {
      openSnackbar({
        color: 'error',
        message: 'Fehler beim Erstellen der Parkanlage.',
      });
    },
  });

  const stationType = stationId ? 'P+R Anlage' : 'Parkraumangebot';
  const tabs = [stationType];
  const { setRef, scrollTo, activeIndex } = useScrollSpy<HTMLDivElement>(tabs.length);

  return (
    <Fragment>
      <AdministrationHeader formId="parkingFacilityForm" label={`${stationType} erstellen`} />
      <AdministrationBody>
        <FormGridLayout activeTab={activeIndex} onTabChange={scrollTo} tabs={tabs}>
          <CreateParkAndRideFacilityForm
            disabled={!ability?.can(Action.MANAGE, Subject.All)}
            organizations={organizations}
            rmvId={stationData?.rmvNumber ? String(stationData.rmvNumber) : undefined}
            setRef={setRef}
            tabs={tabs}
            onSubmit={async (values) => {
              await mutateAsync({ body: { ...values, stationId } });
            }}
          />
        </FormGridLayout>
      </AdministrationBody>
    </Fragment>
  );
}
